import { symmetricKey } from './symmetricKey';
import { hierarchicalLeafOrder } from './hierarchicalOrder';
import {
  CLONAL_SNP_THRESHOLD,
  CLOSELY_RELATED_SNP_THRESHOLD
} from './snpThresholds';

const parsePairKey = (key) => {
  const [leftId, rightId] = key.split(':').map(Number);
  return { leftId, rightId };
};

const clusterLabelForIndex = (index) => {
  let value = index;
  let label = '';

  do {
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26) - 1;
  } while (value >= 0);

  return label;
};

const compareByName = (left, right) => left.name.localeCompare(right.name);

const sortCloseEntries = (left, right) => {
  if (left.kind !== right.kind) {
    return left.kind === 'clonal' ? -1 : 1;
  }

  if (left.kind === 'clonal') {
    return String(left.label || '').localeCompare(String(right.label || ''));
  }

  return String(left.sampleNames[0] || '').localeCompare(String(right.sampleNames[0] || ''));
};

class DisjointSet {
  constructor(ids) {
    this.parent = new Map();
    this.rank = new Map();
    ids.forEach((id) => {
      this.parent.set(id, id);
      this.rank.set(id, 0);
    });
  }

  find(id) {
    const parent = this.parent.get(id);
    if (parent == null) {
      return null;
    }

    if (parent !== id) {
      this.parent.set(id, this.find(parent));
    }

    return this.parent.get(id);
  }

  union(leftId, rightId) {
    const leftRoot = this.find(leftId);
    const rightRoot = this.find(rightId);
    if (leftRoot == null || rightRoot == null || leftRoot === rightRoot) {
      return;
    }

    const leftRank = this.rank.get(leftRoot) || 0;
    const rightRank = this.rank.get(rightRoot) || 0;

    if (leftRank < rightRank) {
      this.parent.set(leftRoot, rightRoot);
      return;
    }
    if (leftRank > rightRank) {
      this.parent.set(rightRoot, leftRoot);
      return;
    }

    this.parent.set(rightRoot, leftRoot);
    this.rank.set(leftRoot, leftRank + 1);
  }
}

export const clusterSamples = ({
  samples,
  comparisons,
  clonalThreshold = CLONAL_SNP_THRESHOLD,
  closelyRelatedThreshold = CLOSELY_RELATED_SNP_THRESHOLD
}) => {
  const sampleIds = samples.map((sample) => sample.id);
  const sampleById = new Map(samples.map((sample) => [sample.id, sample]));
  const dsu = new DisjointSet(sampleIds);
  const distanceByKey = new Map();

  comparisons.forEach((entry, key) => {
    const value = entry?.value;
    if (value != null && !Number.isNaN(value)) {
      distanceByKey.set(key, value);
    }
    if (value == null || Number.isNaN(value) || value > clonalThreshold) {
      return;
    }

    const { leftId, rightId } = parsePairKey(key);
    if (Number.isNaN(leftId) || Number.isNaN(rightId) || leftId === rightId) {
      return;
    }

    dsu.union(leftId, rightId);
  });

  const getDistance = (leftId, rightId) => {
    if (leftId === rightId) {
      return 0;
    }
    const distance = distanceByKey.get(symmetricKey(leftId, rightId));
    return distance == null || Number.isNaN(distance) ? Infinity : distance;
  };

  const memberIdsByRoot = new Map();
  sampleIds.forEach((id) => {
    const root = dsu.find(id);
    if (root == null) {
      return;
    }

    if (!memberIdsByRoot.has(root)) {
      memberIdsByRoot.set(root, []);
    }
    memberIdsByRoot.get(root).push(id);
  });

  const groupedSamples = [...memberIdsByRoot.entries()]
    .map(([rootId, ids]) => ({
      rootId,
      samples: ids
        .map((id) => sampleById.get(id))
        .filter(Boolean)
        .sort(compareByName)
    }))
    .sort((leftGroup, rightGroup) => {
      if (rightGroup.samples.length !== leftGroup.samples.length) {
        return rightGroup.samples.length - leftGroup.samples.length;
      }

      const leftFirstName = leftGroup.samples[0]?.name || '';
      const rightFirstName = rightGroup.samples[0]?.name || '';
      return leftFirstName.localeCompare(rightFirstName);
    });

  let labeledClusterCount = 0;
  const clusters = groupedSamples.map((group, index) => {
    const kind = group.samples.length >= 2 ? 'clonal' : 'singleton';
    return {
      id: index,
      kind,
      label: kind === 'clonal' ? clusterLabelForIndex(labeledClusterCount++) : null,
      sampleIds: group.samples.map((sample) => sample.id),
      size: group.samples.length
    };
  });

  const clusterById = new Map(clusters.map((cluster) => [cluster.id, cluster]));
  const clusterIdBySampleId = new Map();
  const clusterStartIndexById = new Map();
  let sampleOffset = 0;
  clusters.forEach((cluster) => {
    clusterStartIndexById.set(cluster.id, sampleOffset);
    cluster.sampleIds.forEach((sampleId) => {
      clusterIdBySampleId.set(sampleId, cluster.id);
    });
    sampleOffset += cluster.size;
  });

  const closeBySampleId = new Map();
  const closeSingletonsBySampleId = new Map();
  const addCloseRelationship = (sampleId, relatedClusterId) => {
    const relatedCluster = clusterById.get(relatedClusterId);
    if (!relatedCluster) {
      return;
    }

    if (!closeBySampleId.has(sampleId)) {
      closeBySampleId.set(sampleId, new Map());
    }

    const byClusterId = closeBySampleId.get(sampleId);
    if (byClusterId.has(relatedClusterId)) {
      return;
    }

    const sampleNames = relatedCluster.sampleIds
      .map((id) => sampleById.get(id)?.name || '')
      .filter(Boolean)
      .sort((left, right) => left.localeCompare(right));

    byClusterId.set(relatedClusterId, {
      clusterId: relatedCluster.id,
      label: relatedCluster.label,
      kind: relatedCluster.kind,
      sampleNames
    });
  };
  const addCloseSingletonRelationship = (sampleId, relatedSampleId, distance) => {
    const relatedSample = sampleById.get(relatedSampleId);
    if (!relatedSample) {
      return;
    }

    if (!closeSingletonsBySampleId.has(sampleId)) {
      closeSingletonsBySampleId.set(sampleId, new Map());
    }

    const byRelatedSampleId = closeSingletonsBySampleId.get(sampleId);
    if (byRelatedSampleId.has(relatedSampleId)) {
      return;
    }

    byRelatedSampleId.set(relatedSampleId, {
      sampleId: relatedSampleId,
      sampleName: relatedSample.name,
      distance
    });
  };

  comparisons.forEach((entry, key) => {
    const value = entry?.value;
    if (value == null || Number.isNaN(value) || value <= clonalThreshold || value > closelyRelatedThreshold) {
      return;
    }

    const { leftId, rightId } = parsePairKey(key);
    if (Number.isNaN(leftId) || Number.isNaN(rightId) || leftId === rightId) {
      return;
    }

    const leftClusterId = clusterIdBySampleId.get(leftId);
    const rightClusterId = clusterIdBySampleId.get(rightId);
    if (leftClusterId == null || rightClusterId == null || leftClusterId === rightClusterId) {
      return;
    }

    addCloseRelationship(leftId, rightClusterId);
    addCloseRelationship(rightId, leftClusterId);

    const leftCluster = clusterById.get(leftClusterId);
    const rightCluster = clusterById.get(rightClusterId);
    if (leftCluster?.kind !== 'singleton' || rightCluster?.kind !== 'singleton') {
      return;
    }

    addCloseSingletonRelationship(leftId, rightId, value);
    addCloseSingletonRelationship(rightId, leftId, value);
  });

  // Propagate default SNP closely-related cluster links to every member of a clonal cluster.
  clusters.forEach((cluster) => {
    if (cluster.kind !== 'clonal' || cluster.size < 2) {
      return;
    }

    cluster.sampleIds.forEach((sampleId) => {
      const byClusterId = closeBySampleId.get(sampleId);
      if (!byClusterId) {
        return;
      }

      byClusterId.forEach((entry, relatedClusterId) => {
        cluster.sampleIds.forEach((memberId) => {
          if (memberId !== sampleId) {
            addCloseRelationship(memberId, relatedClusterId);
          }
        });
      });
    });
  });

  const closelyRelatedBySampleId = new Map();
  sampleIds.forEach((sampleId) => {
    const byClusterId = closeBySampleId.get(sampleId);
    closelyRelatedBySampleId.set(
      sampleId,
      byClusterId ? [...byClusterId.values()].sort(sortCloseEntries) : []
    );
  });

  const sortSingletonEntries = (left, right) => {
    if (left.distance !== right.distance) {
      return left.distance - right.distance;
    }
    return left.sampleName.localeCompare(right.sampleName);
  };

  const closelySingletonsBySampleId = new Map();
  sampleIds.forEach((sampleId) => {
    const byRelatedSampleId = closeSingletonsBySampleId.get(sampleId);
    closelySingletonsBySampleId.set(
      sampleId,
      byRelatedSampleId ? [...byRelatedSampleId.values()].sort(sortSingletonEntries) : []
    );
  });

  const orderedSamples = hierarchicalLeafOrder(samples, getDistance);
  const clusterOrderedSamples = clusters.flatMap((cluster) => (
    cluster.sampleIds.map((sampleId) => sampleById.get(sampleId)).filter(Boolean)
  ));

  return {
    orderedSamples,
    clusterOrderedSamples,
    clusters,
    clusterIdBySampleId,
    clusterStartIndexById,
    closelyRelatedBySampleId,
    closelySingletonsBySampleId
  };
};

export default clusterSamples;
